/**
 * Signup Service
 *
 * Handles all signup operations with TRUE atomicity using Firestore transactions.
 * 
 * KEY FIXES for race conditions:
 * 1. Deterministic signup doc IDs (classId_emailHash) enable transaction.get() for duplicate check
 * 2. signupCount counter on class docs for atomic capacity enforcement
 * 
 * This prevents the race conditions where getDocs() queries could return stale data.
 */

import {
  doc,
  runTransaction,
  serverTimestamp,
  Firestore,
  increment,
} from "firebase/firestore";

export interface SignupRequest {
  classId: string;
  firstName: string;
  lastName: string;
  email: string;
  paymentMethod: "venmo" | "arrangement" | "none";
  paymentStatus: "pending" | "completed" | "venmo";
  status: string;
  skipConfirmationEmail?: boolean;
  isBatchSignup?: boolean;
}

export interface SignupResult {
  success: boolean;
  signupId?: string;
  error?: "ALREADY_SIGNED_UP" | "CLASS_FULL" | "CLASS_NOT_FOUND" | "UNKNOWN";
  message?: string;
}

export interface CancelResult {
  success: boolean;
  error?: "NOT_FOUND" | "UNKNOWN";
  message?: string;
}

/**
 * Custom error class for signup-related errors
 */
class SignupError extends Error {
  code: string;
  constructor(code: string, message?: string) {
    super(message || code);
    this.code = code;
    this.name = 'SignupError';
  }
}

/**
 * Normalize email for consistent matching
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Generate a deterministic signup document ID from classId and email.
 * This allows us to use transaction.get() for truly atomic duplicate detection.
 */
function generateSignupId(classId: string, email: string): string {
  const normalizedEmail = normalizeEmail(email);
  // Replace characters that aren't valid in Firestore doc IDs
  const safeEmail = normalizedEmail.replace(/[.@]/g, '_');
  return `${classId}_${safeEmail}`;
}

/**
 * Create a signup with TRULY atomic duplicate and capacity checking.
 * 
 * Uses:
 * - Deterministic doc IDs for atomic duplicate detection via transaction.get()
 * - signupCount field on class doc for atomic capacity enforcement
 *
 * @param db - Firestore instance
 * @param appId - Application ID for multi-tenant support
 * @param request - Signup data
 * @param options.bypassCapacity - If true, allows overbooking (for admin use)
 */
export async function createSignup(
  db: Firestore,
  appId: string,
  request: SignupRequest,
  options: { bypassCapacity?: boolean } = {}
): Promise<SignupResult> {
  const normalizedEmail = normalizeEmail(request.email);
  const signupId = generateSignupId(request.classId, normalizedEmail);
  
  const signupRef = doc(
    db,
    "artifacts",
    appId,
    "public",
    "data",
    "signups",
    signupId
  );
  const classRef = doc(
    db,
    "artifacts",
    appId,
    "public",
    "data",
    "classes",
    request.classId
  );

  try {
    await runTransaction(db, async (transaction) => {
      // 1. Fetch class to check capacity and verify it exists (ATOMIC READ)
      const classSnap = await transaction.get(classRef);
      if (!classSnap.exists()) {
        throw new SignupError("CLASS_NOT_FOUND");
      }
      const classData = classSnap.data();
      const capacity =
        typeof classData.capacity === "string"
          ? parseInt(classData.capacity, 10)
          : classData.capacity;
      const currentCount = classData.signupCount || 0;

      // 2. Check for existing signup (ATOMIC READ with deterministic ID)
      const existingSignup = await transaction.get(signupRef);
      if (existingSignup.exists()) {
        throw new SignupError("ALREADY_SIGNED_UP");
      }

      // 3. Check capacity atomically (unless admin bypass is enabled)
      if (!options.bypassCapacity && capacity > 0) {
        if (currentCount >= capacity) {
          throw new SignupError("CLASS_FULL");
        }
      }

      // 4. Create the signup document with deterministic ID
      transaction.set(signupRef, {
        classId: request.classId,
        firstName: request.firstName,
        lastName: request.lastName,
        email: normalizedEmail,
        paymentMethod: request.paymentMethod,
        paymentStatus: request.paymentStatus,
        status: request.status,
        timestamp: serverTimestamp(),
        ...(request.skipConfirmationEmail
          ? { skipConfirmationEmail: true }
          : {}),
        ...(request.isBatchSignup
          ? { isBatchSignup: true }
          : {}),
      });

      // 5. Increment signup count on class (ATOMIC WRITE)
      transaction.update(classRef, {
        signupCount: increment(1)
      });
    });

    return { success: true, signupId };
  } catch (error: any) {
    if (error.code === "ALREADY_SIGNED_UP") {
      return {
        success: false,
        error: "ALREADY_SIGNED_UP",
        message: "You're already signed up for this class!",
      };
    }
    if (error.code === "CLASS_FULL") {
      return {
        success: false,
        error: "CLASS_FULL",
        message: "Sorry, this class is now full.",
      };
    }
    if (error.code === "CLASS_NOT_FOUND") {
      return {
        success: false,
        error: "CLASS_NOT_FOUND",
        message: "This class no longer exists.",
      };
    }
    console.error("Signup transaction failed:", error);
    return {
      success: false,
      error: "UNKNOWN",
      message: "Error booking class. Please try again.",
    };
  }
}

/**
 * Cancel a signup atomically.
 * 
 * Uses transaction to ensure signup exists before deleting and decrements counter.
 *
 * @param db - Firestore instance
 * @param appId - Application ID
 * @param classId - The class to cancel from
 * @param email - User's email (will be normalized)
 */
export async function cancelSignup(
  db: Firestore,
  appId: string,
  classId: string,
  email: string
): Promise<CancelResult> {
  const normalizedEmail = normalizeEmail(email);
  const signupId = generateSignupId(classId, normalizedEmail);
  
  const signupRef = doc(
    db,
    "artifacts",
    appId,
    "public",
    "data",
    "signups",
    signupId
  );
  const classRef = doc(
    db,
    "artifacts",
    appId,
    "public",
    "data",
    "classes",
    classId
  );

  try {
    await runTransaction(db, async (transaction) => {
      // 1. ALL READS FIRST (Firestore requires reads before writes)
      const signupSnap = await transaction.get(signupRef);
      const classSnap = await transaction.get(classRef);
      
      // 2. Validate signup exists
      if (!signupSnap.exists()) {
        throw new SignupError("NOT_FOUND");
      }

      // 3. ALL WRITES AFTER READS
      // Delete the signup
      transaction.delete(signupRef);

      // Decrement signup count on class (only if class still exists)
      if (classSnap.exists()) {
        transaction.update(classRef, {
          signupCount: increment(-1)
        });
      }
    });

    return { success: true };
  } catch (error: any) {
    if (error.code === "NOT_FOUND") {
      return {
        success: false,
        error: "NOT_FOUND",
        message: "No signup found to cancel.",
      };
    }
    console.error("Cancel signup failed:", error);
    return {
      success: false,
      error: "UNKNOWN",
      message: "Error cancelling signup. Please try again.",
    };
  }
}
